export function reset_signing_request_state(vm) {
    vm.current_request_step = 1;
    vm.selected_template = null;
    vm.signer_configuration_summary = null;
    vm.signers_step_busy = false;
    vm.populated_preview_url = "";
    vm.populated_preview_external_url = "";
    vm.populated_preview_form_src = "";
    vm.populated_preview_token = "";
    vm.populated_preview_loading = false;
    vm.populated_preview_error = "";
    vm.sending_contract = false;
}

export function normalize_text(value) {
    return String(value || "").trim();
}

export function normalize_match_text(value) {
    return normalize_text(value).toLowerCase().replace(/[^a-z0-9]+/g, " ");
}

function normalize_search_text(value) {
    return normalize_text(value).toLowerCase();
}

export function unique_subtitle_parts(parts = []) {
    const seen_parts = new Set();
    return parts
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .filter((part) => {
            const normalized_part = normalize_search_text(part);
            if (seen_parts.has(normalized_part)) {
                return false;
            }
            seen_parts.add(normalized_part);
            return true;
        });
}

export function get_first_text(values = [], fallback = "-") {
    const value = values.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== "");
    return value === undefined ? fallback : String(value);
}

export function normalize_answer_values(answer) {
    if (Array.isArray(answer)) {
        return answer.map((item) => String(item || "").trim()).filter(Boolean);
    }

    const normalized = String(answer ?? "").trim();
    return normalized ? [normalized] : [];
}

export function normalize_historical_risk_answer(answer = {}, index = 0) {
    return {
        id: Number(answer?.question_id || answer?.id || index + 1),
        label: get_first_text([
            answer?.question_text,
            answer?.question,
            answer?.label,
            answer?.name
        ], "Risk question"),
        selected_answer: get_first_text([
            answer?.answer,
            answer?.display_value,
            answer?.value,
            answer?.response
        ], "Not answered")
    };
}

export function get_historical_risk_question_definitions(case_item = {}) {
    const threshold_questions = Array.isArray(case_item?.threshold_assessment?.questions)
        ? case_item.threshold_assessment.questions
        : [];
    const result_questions = Array.isArray(case_item?.risk_result?.questions)
        ? case_item.risk_result.questions
        : [];
    return threshold_questions.length ? threshold_questions : result_questions;
}

export function get_historical_risk_choice_rows(question = {}, selected_answers = []) {
    const choices = Array.isArray(question?.choices) ? question.choices : [];
    return choices.map((choice) => {
        const option = String(choice?.option || choice?.label || choice?.value || "").trim();
        return {
            option,
            weight: Number(choice?.weight || choice?.score || 0),
            is_selected: selected_answers.includes(option)
        };
    });
}

export function get_historical_risk_max_score(question = {}) {
    const choices = Array.isArray(question?.choices) ? question.choices : [];
    return choices.reduce((max, choice) => Math.max(max, Number(choice?.weight || choice?.score || 0)), 0);
}

export function get_historical_risk_fallback_items(case_item = {}) {
    const factor_sets = [
        ...(Array.isArray(case_item?.risk_result?.failed_rules) ? case_item.risk_result.failed_rules : []),
        ...(Array.isArray(case_item?.risk_result?.failed_checks) ? case_item.risk_result.failed_checks : []),
        ...(Array.isArray(case_item?.risk_result?.risk_factors) ? case_item.risk_result.risk_factors : []),
        ...(Array.isArray(case_item?.threshold_assessment?.factors) ? case_item.threshold_assessment.factors : [])
    ];

    return factor_sets.map((item, index) => ({
        id: String(item?.id || item?.question_id || `risk-factor-${index}`),
        label: get_first_text([item?.label, item?.name, item?.rule_name, item?.question], "Risk factor"),
        selected_answer: get_first_text([item?.answer, item?.detail, item?.reason, item?.description], "-"),
        selected_score: Number(item?.score || 0),
        max_score: Number(item?.max_score || item?.weight || 0),
        comparison_percent: Number(item?.comparison_percent || 0),
        choices: []
    }));
}

export function resolve_historical_risk_assessment(case_item = {}) {
    const risk_result = case_item?.risk_result || {};
    const assessment_id = Number(
        risk_result?.assessment_id
        || risk_result?.risk_framework_assessment_id
        || case_item?.assessment_id
        || 0
    );
    if (!assessment_id) {
        return null;
    }

    return {
        id: assessment_id,
        name: get_first_text([
            risk_result?.assessment_name,
            case_item?.threshold_assessment?.assessment_name,
            case_item?.threshold_assessment?.name
        ], "Risk assessment")
    };
}

export function build_historical_risk_weight_lookup(weights = []) {
    const lookup = new Map();
    weights.forEach((item) => {
        const question_id = Number(item?.question_id || 0);
        const choices = Array.isArray(item?.choices) ? item.choices : [];
        choices.forEach((choice) => {
            lookup.set(`${question_id}:${String(choice?.option || "").trim()}`, Number(choice?.weight || 0));
        });
    });
    return lookup;
}

export function build_historical_risk_choice_rows(question_id, weights = [], selected_answers = []) {
    const item = weights.find((weight_item) => Number(weight_item?.question_id || 0) === question_id);
    const choices = Array.isArray(item?.choices) ? item.choices : [];
    return choices.map((choice) => {
        const option = String(choice?.option || "").trim();
        return {
            option,
            weight: Number(choice?.weight || 0),
            is_selected: selected_answers.includes(option)
        };
    });
}

export function get_historical_risk_weight_max(question_id, weights = []) {
    return build_historical_risk_choice_rows(question_id, weights)
        .reduce((max, choice) => Math.max(max, Number(choice.weight || 0)), 0);
}

function get_case_stage_candidates(case_item = {}) {
    const workflow_statuses = Array.isArray(case_item?.workflow_statuses) ? case_item.workflow_statuses : [];
    return [
        case_item?.workflow_display_stage,
        case_item?.current_workflow_status,
        case_item?.stage,
        case_item?.detail,
        case_item?.activation,
        ...workflow_statuses.map((status) => (
            status?.status_name
            || status?.workflow_status_name
            || status?.name
            || status?.status
        ))
    ].filter(Boolean);
}

export function matches_esigning_gate(case_item = {}) {
    return get_case_stage_candidates(case_item).some((value) => {
        const normalized_value = normalize_match_text(value);
        return normalized_value.includes("e signing")
            || normalized_value.includes("esigning")
            || normalized_value.includes("electronic sign")
            || normalized_value.includes("signing");
    });
}

export function matches_application_id(case_item = {}, application_id = 0) {
    return Number(case_item?.application_id || case_item?.id || 0) === Number(application_id || 0);
}

export function matches_entity_profile_id(case_item = {}, entity_profile_id = 0) {
    return Number(
        case_item?.entity_profile_id
        || case_item?.entity_profile?.id
        || case_item?.profile?.entity_profile_id
        || case_item?.profile?.id
        || 0
    ) === Number(entity_profile_id || 0);
}

export function build_preserved_route_query(route = {}) {
    const next_query = {};
    if (route?.query?.journey) next_query.journey = String(route.query.journey);
    if (route?.query?.journey_label) next_query.journey_label = String(route.query.journey_label);
    return next_query;
}

export function clear_signing_context_data(vm) {
    vm.template_bindings = [];
    vm.template_bindings_error = "";
    vm.selected_template = null;
    vm.sent_contracts = [];
    vm.sent_contracts_error = "";
    vm.entity_config_id = null;
    vm.historical_gate_checks_by_gate = {};
}

export function reset_case_summary_data(vm) {
    vm.selected_case_summary = null;
    vm.active_summary_detail_tab = "summary";
    vm.is_summary_detail_fullscreen = false;
    vm.is_loading_summary_detail = false;
    vm.summary_rows = [];
    vm.summary_stage_timeline = [];
    vm.summary_case_form_sections = [];
}

export function get_selected_case_route(vm, application_id) {
    const query = build_preserved_route_query(vm.$route);
    if (String(vm.$route?.query?.startNewSigning || "") === "1") {
        query.startNewSigning = "1";
    }

    return {
        name: "OnboardingEsigningCase",
        params: { applicationId: String(application_id) },
        query
    };
}

export function set_sent_contracts_error(vm, message) {
    vm.sent_contracts = [];
    vm.sent_contracts_error = message;
}

export function show_system_loader(message) {
    window.dispatchEvent(new CustomEvent("system-loader:show", { detail: message }));
}

export function hide_system_loader_after_render(vm) {
    vm.$nextTick(() => {
        window.dispatchEvent(new CustomEvent("system-loader:hide"));
    });
}

export function has_cached_gate_cases(vm) {
    return vm.initialized_gate_cases && Array.isArray(vm.all_cases) && vm.all_cases.length > 0;
}

export function handle_sent_contracts_response(vm, parsed_response, has_failed_response) {
    if (has_failed_response(parsed_response)) {
        set_sent_contracts_error(vm, parsed_response?.message || "Failed to load sent contracts.");
        return;
    }

    vm.sent_contracts = Array.isArray(parsed_response?.data) ? parsed_response.data : [];
}

export function get_sent_contracts_error_message(vm, error) {
    const response_payload = vm.parse_response_data(error?.response?.data);
    return response_payload?.message || error?.message || "Failed to load sent contracts.";
}
